// src/hooks/useBlacklist.js
// Firestore CRUD hook for the blacklist collection.
// Schema: { uid, email, phone, ip, reason, notes, active, createdAt, createdBy }
// A document may carry more than one identifier simultaneously.

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

export const BL_PAGE_SIZE = 25;

// ─── Identifier field definitions (shared across components) ──────────────────
export const IDENTIFIER_FIELDS = [
  { key: 'uid',   label: 'UID',   color: '#8B5CF6' },
  { key: 'email', label: 'EMAIL', color: '#3B82F6' },
  { key: 'phone', label: 'PHONE', color: '#22C55E' },
  { key: 'ip',    label: 'IP',    color: '#F59E0B' },
];

// ─── Parse any Firestore date ─────────────────────────────────────────────────
export const parseBlDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === 'string') { const d = new Date(val); return isNaN(d) ? null : d; }
  return null;
};

let _loggedFirstDoc = false;

// ─── Map Firestore doc → clean entry ─────────────────────────────────────────
const mapEntry = (snap) => {
  const d = snap.data();

  // Diagnostic: log the first document's raw fields once to help catch mismatches
  if (!_loggedFirstDoc) {
    _loggedFirstDoc = true;
    console.log('[useBlacklist] First document fields:', Object.keys(d));
    console.log('[useBlacklist] First document values:', {
      uid:       d.uid,
      email:     d.email,
      phone:     d.phone,
      ip:        d.ip,
      reason:    d.reason,
      active:    d.active,
      createdAt: d.createdAt,
      createdBy: d.createdBy,
    });
  }

  return {
    _id:       snap.id,
    uid:       d.uid       || '',
    email:     d.email     || '',
    phone:     d.phone     || '',
    ip:        d.ip        || '',
    reason:    d.reason    || '',
    notes:     d.notes     || '',
    createdAt: parseBlDate(d.createdAt),
    createdBy: d.createdBy || '',
    active:    d.active !== false, // defaults true
    rawDoc:    d,
  };
};

// ─── Main Hook ────────────────────────────────────────────────────────────────
const useBlacklist = () => {
  const [allEntries, setAllEntries]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [opLoading, setOpLoading]         = useState(false);
  const [error, setError]                 = useState(null);
  const [toast, setToast]                 = useState(null);

  // Filter state
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');   // all | active | disabled
  const [typeFilter, setTypeFilter]       = useState('all');   // all | uid | email | phone | ip

  // Pagination
  const [page, setPage] = useState(1);

  // Dialog state
  const [viewEntry, setViewEntry]         = useState(null);
  const [editEntry, setEditEntry]         = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [dialogOpen, setDialogOpen]       = useState(false);
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [viewOpen, setViewOpen]           = useState(false);

  const fetchId = useRef(0);

  // ── Fetch all entries ──────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);

    console.log('[useBlacklist] Starting fetch from collection: blacklist');

    // ── Attempt 1: ordered query ─────────────────────────────────────────────
    try {
      const q = query(collection(db, 'blacklist'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      console.log(`[useBlacklist] Ordered fetch returned ${snap.size} document(s)`);
      if (id !== fetchId.current) return;
      setAllEntries(snap.docs.map(mapEntry));
      setLoading(false);
      return; // ← done
    } catch (err1) {
      console.warn('[useBlacklist] Ordered query failed:', err1.code, err1.message);
      // If it was a permissions error, stop here — fallback won't help
      if (
        err1.code === 'permission-denied' ||
        err1.code === 'unauthenticated'
      ) {
        if (id === fetchId.current) {
          setError(`Firestore permission denied on 'blacklist': ${err1.message}`);
          setLoading(false);
        }
        return;
      }
      // Otherwise fall through to unordered fetch
    }

    // ── Attempt 2: unordered fetch (no index required) ───────────────────────
    try {
      const snap = await getDocs(collection(db, 'blacklist'));
      console.log(`[useBlacklist] Unordered fetch returned ${snap.size} document(s)`);
      if (id !== fetchId.current) return;

      // Sort client-side by createdAt descending
      const entries = snap.docs.map(mapEntry);
      entries.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt - a.createdAt;
      });

      setAllEntries(entries);
    } catch (err2) {
      console.error('[useBlacklist] Unordered fetch also failed:', err2.code, err2.message);
      if (id === fetchId.current) {
        setError(`Failed to load blacklist: ${err2.message}`);
      }
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toast helper ───────────────────────────────────────────────────────────
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Summary stats ──────────────────────────────────────────────────────────
  const stats = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let active = 0, disabled = 0, todayCount = 0;
    allEntries.forEach((e) => {
      if (e.active) active++; else disabled++;
      if (e.createdAt && e.createdAt >= today) todayCount++;
    });
    return { total: allEntries.length, active, disabled, today: todayCount };
  })();

  // ── Client-side filter + search ────────────────────────────────────────────
  const filtered = (() => {
    const q = search.trim().toLowerCase();
    return allEntries.filter((e) => {
      if (statusFilter === 'active'   && !e.active) return false;
      if (statusFilter === 'disabled' &&  e.active) return false;
      if (typeFilter !== 'all' && !e[typeFilter])   return false;
      if (q) {
        const haystack = [e.uid, e.email, e.phone, e.ip, e.reason, e.notes, e.createdBy]
          .join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  })();

  const totalPages  = Math.max(1, Math.ceil(filtered.length / BL_PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const pageEntries = filtered.slice((safePage - 1) * BL_PAGE_SIZE, safePage * BL_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter]);

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const addEntry = useCallback(async (formData) => {
    setOpLoading(true);
    try {
      const currentUser = auth.currentUser;
      const payload = {
        uid:       formData.uid.trim()   || null,
        email:     formData.email.trim() || null,
        phone:     formData.phone.trim() || null,
        ip:        formData.ip.trim()    || null,
        reason:    formData.reason.trim(),
        notes:     formData.notes.trim() || null,
        active:    formData.active,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.email || currentUser?.uid || 'admin',
      };
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);

      const ref = await addDoc(collection(db, 'blacklist'), payload);
      // Build a snap-compatible object so mapEntry works correctly
      const fakeSnap = { id: ref.id, data: () => payload };
      const newEntry = mapEntry(fakeSnap);
      setAllEntries((prev) => [newEntry, ...prev]);
      setDialogOpen(false);
      showToast('success', 'Entry added to blacklist');
    } catch (err) {
      showToast('error', `Failed to add: ${err.message}`);
    } finally {
      setOpLoading(false);
    }
  }, []);

  const updateEntry = useCallback(async (id, formData) => {
    setOpLoading(true);
    try {
      const payload = {
        uid:       formData.uid.trim()       || null,
        email:     formData.email.trim()     || null,
        phone:     formData.phone.trim()     || null,
        ip:        formData.ip.trim()        || null,
        reason:    formData.reason.trim(),
        notes:     formData.notes.trim()     || null,
        active:    formData.active,
        updatedAt: Timestamp.now(),
      };
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);

      await updateDoc(doc(db, 'blacklist', id), payload);
      setAllEntries((prev) =>
        prev.map((e) =>
          e._id === id
            ? { ...e, ...payload,
                uid:   payload.uid   || '',
                email: payload.email || '',
                phone: payload.phone || '',
                ip:    payload.ip    || '',
                notes: payload.notes || '',
                createdAt: e.createdAt }
            : e
        )
      );
      setDialogOpen(false);
      setEditEntry(null);
      showToast('success', 'Entry updated');
    } catch (err) {
      showToast('error', `Failed to update: ${err.message}`);
    } finally {
      setOpLoading(false);
    }
  }, []);

  const toggleActive = useCallback(async (id, currentActive) => {
    setOpLoading(true);
    try {
      await updateDoc(doc(db, 'blacklist', id), { active: !currentActive, updatedAt: Timestamp.now() });
      setAllEntries((prev) =>
        prev.map((e) => e._id === id ? { ...e, active: !currentActive } : e)
      );
      showToast('success', !currentActive ? 'Entry enabled' : 'Entry disabled');
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setOpLoading(false);
    }
  }, []);

  const deleteEntry = useCallback(async (id) => {
    setOpLoading(true);
    try {
      await deleteDoc(doc(db, 'blacklist', id));
      setAllEntries((prev) => prev.filter((e) => e._id !== id));
      setDeleteOpen(false);
      setDeleteTarget(null);
      showToast('success', 'Entry deleted');
    } catch (err) {
      showToast('error', `Failed to delete: ${err.message}`);
    } finally {
      setOpLoading(false);
    }
  }, []);

  // ── Dialog helpers ─────────────────────────────────────────────────────────
  const openAdd           = () => { setEditEntry(null); setDialogOpen(true); };
  const openEdit          = (entry) => { setEditEntry(entry); setDialogOpen(true); };
  const openView          = (entry) => { setViewEntry(entry); setViewOpen(true); };
  const openDeleteConfirm = (entry) => { setDeleteTarget(entry); setDeleteOpen(true); };

  return {
    allEntries, pageEntries, filtered, stats,
    loading, opLoading, error, toast,
    search, setSearch,
    statusFilter, setStatusFilter,
    typeFilter, setTypeFilter,
    page: safePage, totalPages, setPage,
    hasNext: safePage < totalPages,
    hasPrev: safePage > 1,
    fetchAll,
    addEntry, updateEntry, toggleActive, deleteEntry,
    dialogOpen, setDialogOpen,
    editEntry,
    viewEntry,
    viewOpen, setViewOpen,
    deleteTarget,
    deleteOpen, setDeleteOpen,
    openAdd, openEdit, openView, openDeleteConfirm,
  };
};

export default useBlacklist;
